import React, { useState, useEffect, useRef } from 'react';
import { RewriteResumeResponse } from '@/api/types';
import { ATSResumeJson } from '@/types/resume';
import { pdf } from '@react-pdf/renderer';
import { AtsResumePDF } from '@/components/common/AtsResumePDF';
import { handleAppError } from '@/lib/errorUtils';
import { generateRefinedResume } from '@/services/geminiService';
import { cleanTagsFromData } from './utils';
import { extractLinksFromPdf } from './pdfExtractor';

interface UseTailoredResumeProps {
  result: RewriteResumeResponse;
  resume?: any;
  jobText?: string;
  recommendations?: any[];
  onUpdateResult?: (res: RewriteResumeResponse) => void;
}

export function useTailoredResume({
  result,
  resume,
  jobText,
  recommendations,
  onUpdateResult
}: UseTailoredResumeProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [resumeData, setResumeData] = useState<any>(result.history ? result.history[result.history.length - 1] : result.resumeJson);
  const [history, setHistory] = useState<any[]>(result.history || [result.resumeJson]);
  const [historyIndex, setHistoryIndex] = useState<number>(result.history ? result.history.length - 1 : 0);
  
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showChanges, setShowChanges] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const [isEditingJson, setIsEditingJson] = useState(false);
  const [editingData, setEditingData] = useState<ATSResumeJson | null>(null);

  // Sync state if a new tailoring runs or when loading from saved history
  useEffect(() => {
    if (result && result.history) {
      const lastItem = result.history[result.history.length - 1];
      if (lastItem !== history[history.length - 1]) {
        setHistory(result.history);
        setHistoryIndex(result.history.length - 1);
        setResumeData(lastItem);
        setFeedback('');
        setError('');
      }
    } else if (result && result.resumeJson !== history[0] && !result.history) {
      setResumeData(result.resumeJson);
      setHistory([result.resumeJson]);
      setHistoryIndex(0);
      setFeedback('');
      setError('');
    }
  }, [result]);

  useEffect(() => {
    if (resumeData) {
      let isSubscribed = true;
      pdf(<AtsResumePDF data={resumeData} showChanges={showChanges} />).toBlob()
        .then(blob => {
          if (isSubscribed) {
            setPdfBlob(blob);
          }
        })
        .catch(err => {});
      
      return () => { isSubscribed = false; };
    }
  }, [resumeData, showChanges]);

  const handleGenerate = async (customInstructions?: string) => {
    if (!resume || !jobText) return; 
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    try {
      setIsGenerating(true);
      setError('');
      setPdfBlob(null);

      let extractedUrlsText = '';
      if (resume.contentBase64 && historyIndex < 0) {
        const extractedLinks = await extractLinksFromPdf(resume.contentBase64);
        if (extractedLinks.length > 0) {
          const formattedLinks = extractedLinks.map((l) => `- URL: ${l.url} (Found near text: "${l.context}")`).join('\n');
          extractedUrlsText = `\nCRITICAL: The following hidden URLs were extracted from the PDF WITH their surrounding text. You MUST assign them to the correct header and project by matching the surrounding text to the project name. Ensure NO URL is lost:\n${formattedLinks}\n\nINSTRUCTION: In your response JSON, you MUST fill out the 'url' field for EVERY project that has a corresponding GitHub or deployment URL listed above! Do NOT leave project URLs blank if they are present!`;
          window.dispatchEvent(new CustomEvent('resumepro:show-toast', { detail: `DEBUG - Found ${extractedLinks.length} URLs in PDF.` }));
        } else {
           window.dispatchEvent(new CustomEvent('resumepro:show-toast', { detail: 'DEBUG - No URLs found in PDF.' }));
        }
      }

      const currentContext = historyIndex >= 0 && customInstructions ? history[historyIndex] : undefined;
      
      const modifiedCustomInstructions = extractedUrlsText 
        ? `${extractedUrlsText}\nUser Instructions:\n${customInstructions || ''}`
        : customInstructions;

      const res = await generateRefinedResume(resume, jobText, recommendations || [], modifiedCustomInstructions, currentContext, abortControllerRef.current.signal);
      
      const newResumeData = res.resumeJson;
      const prevHistory = history.slice(0, historyIndex + 1);
      const updatedHistory = [...prevHistory, newResumeData].slice(-10);
      
      setHistory(updatedHistory);
      setHistoryIndex(updatedHistory.length - 1);
      setResumeData(updatedHistory[updatedHistory.length - 1]);
      if (onUpdateResult) {
        onUpdateResult({ ...res, history: updatedHistory, resumeJson: updatedHistory[updatedHistory.length - 1] });
      }
      setFeedback(''); 
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(handleAppError(err, 'Failed to refine resume.'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCancelGenerate = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsGenerating(false);
      setError('Generation canceled by user.');
    }
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setResumeData(history[newIndex]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setResumeData(history[newIndex]);
    }
  };

  const handleDownloadPDF = async () => {
    if (!resumeData) return;
    try {
      setIsDownloading(true);
      const blob = await pdf(<AtsResumePDF data={resumeData} showChanges={false} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      let downloadName = 'Tailored_Resume.pdf';
      if (resume?.name) {
        downloadName = resume.name;
        if (!downloadName.toLowerCase().endsWith('.pdf')) {
          downloadName += '.pdf';
        }
      } else {
        const nameStr = resumeData.basics?.name 
          ? resumeData.basics.name.trim().replace(/\s+/g, '_') 
          : 'Resume';
        downloadName = `${nameStr}.pdf`;
      }
      
      a.download = downloadName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      handleAppError(err, 'Failed to save PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleToggleEditMode = () => {
    if (isEditingJson && editingData) {
      try {
        const updatedData = editingData;
        const prevHistory = history.slice(0, historyIndex + 1);
        const updatedHistory = [...prevHistory, updatedData].slice(-10);
        setHistory(updatedHistory);
        setHistoryIndex(updatedHistory.length - 1);
        setResumeData(updatedData);
        if (onUpdateResult) {
          onUpdateResult({ ...(result as object), history: updatedHistory, resumeJson: updatedData } as RewriteResumeResponse);
        }
        setIsEditingJson(false);
        setError('');
      } catch (err: any) {
        setError('Failed to save data: ' + err.message);
      }
    } else {
      const newEditingData = cleanTagsFromData(resumeData ? JSON.parse(JSON.stringify(resumeData) || '{}') : {});
      setEditingData(newEditingData);
      setIsEditingJson(true);
    }
  };

  const handleSaveToExtension = () => {
    window.postMessage({ type: 'RESUME_PRO_SET_DATA', data: resumeData }, '*');
    window.dispatchEvent(new CustomEvent('resumepro:show-toast', { detail: 'Saved for auto-fill! Use the extension to auto-fill your application.' }));
  };

  return {
    isDownloading,
    resumeData,
    history,
    historyIndex,
    pdfBlob,
    numPages,
    setNumPages,
    isGenerating,
    showChanges,
    setShowChanges,
    zoom,
    setZoom,
    feedback,
    setFeedback,
    error,
    isEditingJson,
    editingData,
    setEditingData,
    handleGenerate,
    handleCancelGenerate,
    handleUndo,
    handleRedo,
    handleDownloadPDF,
    handleToggleEditMode,
    handleSaveToExtension,
    setIsEditingJson
  };
}
